/**
 * Public analytics API.
 *
 * All gtag-facing code in the app should go through this module rather than
 * touching window.gtag / window.dataLayer directly. This keeps our tracking
 * vendor-agnostic, testable, and easy to strip out if we ever migrate to a
 * Rust/WASM or server-side measurement path.
 *
 * The implementation intentionally does NOT swallow errors silently: gtag
 * should already be present (bootstrapped by the inline consent script). If it
 * is not, we log once in non-production so the issue is visible during
 * development but we never crash a user session over an analytics failure.
 */

import type {
  GtagConfigParams,
  GtagEventParams,
} from "./types";
import {
  choicesToSignals,
  hasGlobalPrivacyControl,
  persistConsentChoice,
  readStoredConsent,
  type StoredConsentChoice,
} from "./consent";

export {
  CONSENT_COOKIE_NAME,
  CONSENT_COOKIE_VERSION,
  CONSENT_REQUIRED_REGIONS,
  EU_CONSENT_REGIONS,
  UNKNOWN_COUNTRY_SENTINELS,
  buildConsentBootstrapScript,
  canSilentGrantForCountry,
  choiceToSignals,
  choicesToSignals,
  hasGlobalPrivacyControl,
  persistConsentChoice,
  readStoredConsent,
} from "./consent";
export type {
  ConsentRequiredRegion,
  StoredConsentChoice,
  StoredConsentRecord,
} from "./consent";
export type { ConsentSignals, ConsentState } from "./types";
export {
  detectUserRegion,
  isConsentRequiredForCountry,
  resolveCountryCodeFromRequestHeaders,
} from "./region";
export type { RegionDetectionResult } from "./region";

/**
 * DOM event name used to programmatically re-open the consent banner (e.g.
 * from a "Cookie preferences" link in the footer). Listened for by the
 * `ConsentBanner` component.
 */
export const CONSENT_BANNER_OPEN_EVENT = "nrs:open-consent-banner";

/**
 * DOM event fired after a consent decision is persisted. Used by
 * ``UserIdentityTracker`` so a late US silent-grant (or a later banner
 * accept) can send ``ga_client_id`` without touching authentication.
 */
export const CONSENT_UPDATED_EVENT = "nrs:consent-updated";

/**
 * Programmatically open the consent banner. Safe to call from anywhere in the
 * client — no-op on the server.
 */
export function openConsentBanner(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONSENT_BANNER_OPEN_EVENT));
}

function notifyConsentUpdated(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONSENT_UPDATED_EVENT));
}

function isProduction(): boolean {
  return process.env.NEXT_PUBLIC_NODE_ENV === "production";
}

function warnOnce(key: string, message: string): void {
  if (typeof window === "undefined" || isProduction()) return;
  const w = window as unknown as { __nrs_gtag_warned?: Set<string> };
  if (!w.__nrs_gtag_warned) w.__nrs_gtag_warned = new Set();
  if (w.__nrs_gtag_warned.has(key)) return;
  w.__nrs_gtag_warned.add(key);
  console.warn(`[analytics] ${message}`);
}

/**
 * Returns the gtag identifier for the current environment, or null if not
 * configured. Caller should treat null as "analytics disabled".
 *
 * The returned value may be either a GA4 Measurement ID (`G-XXXXXXXXXX`) or
 * a Google Tag ID (`GT-XXXXXXX`). Both are valid inputs for
 * `gtag.js?id=<...>` and `gtag('config', <...>, {...})`. The Google Tag ID
 * is Google's newer unified tag identifier that can route events to multiple
 * destinations (GA4 properties, Google Ads accounts) configured in the Tag
 * console, without code changes here.
 */
export function getMeasurementId(): string | null {
  const id = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;
  if (typeof id !== "string" || id.trim() === "") return null;
  return id.trim();
}

/**
 * Returns true when the user has explicitly granted analytics_storage
 * (GA4) consent. Unknown / denied states return false. This is the
 * canonical gate for firing GA4 events (client or server-side).
 */
export function hasAnalyticsConsent(): boolean {
  const stored = readStoredConsent();
  return stored?.analytics_storage === "granted";
}

/**
 * Returns true when the user has explicitly granted ad_storage consent.
 * Unknown / denied states return false. This is the canonical gate for
 * firing ads-adjacent events (Reddit CAPI, Google Ads conversions).
 */
export function hasAdStorageConsent(): boolean {
  const stored = readStoredConsent();
  return stored?.ad_storage === "granted";
}

function safeGtag(...args: Parameters<NonNullable<Window["gtag"]>>): void {
  if (typeof window === "undefined") return;
  const fn = window.gtag;
  if (typeof fn !== "function") {
    warnOnce(
      "no-gtag",
      "window.gtag is not available; was the GoogleAnalytics script component mounted?"
    );
    return;
  }
  fn(...args);
}

/**
 * Updates Consent Mode v2 signals based on the user's banner choice and
 * persists the choice to a first-party cookie for subsequent visits.
 *
 * Dual-channel form: callers pass independent `ad_storage` and
 * `analytics_storage` decisions. `ad_storage` drives the three ads signals
 * (ad_storage / ad_user_data / ad_personalization); `analytics_storage`
 * drives its own gtag signal plus the server-side GA4 MP gate.
 *
 * If the user explicitly grants ad_storage while GPC is active, we record a
 * `gpcOverride` flag so the bootstrap script honors the stored ad_storage
 * "granted" on future page loads even with GPC still on
 * (CCPA §7025(c)(6) explicit opt-in). GPC does not apply to analytics.
 */
export function updateConsentGranular(signals: {
  ad_storage: StoredConsentChoice;
  analytics_storage: StoredConsentChoice;
}): void {
  const gtagSignals = choicesToSignals(signals.ad_storage, signals.analytics_storage);
  safeGtag("consent", "update", gtagSignals);
  const gpcActive = hasGlobalPrivacyControl();
  persistConsentChoice(signals, {
    gpcOverride: signals.ad_storage === "granted" && gpcActive,
  });
  notifyConsentUpdated();
}

/**
 * Back-compat shim for call sites that predate the ad_storage /
 * analytics_storage split. Applies the single choice to both channels at
 * once, which reproduces the pre-split behavior.
 *
 * @deprecated Use updateConsentGranular({ ad_storage, analytics_storage }).
 */
export function updateConsent(choice: StoredConsentChoice): void {
  updateConsentGranular({ ad_storage: choice, analytics_storage: choice });
}

/**
 * Sends a typed event to GA4.
 *
 * Per GA4 recommendations, event names are snake_case verbs and stay under 40
 * characters. Reserved names (sign_up, purchase, view_item, page_view, etc.)
 * preserve their GA4-defined schema when used.
 */
export function trackEvent(name: string, params?: GtagEventParams): void {
  safeGtag("event", name, params ?? {});
}

/**
 * Manually fires a GA4 page_view. Required for Next.js App Router because we
 * initialize GA with send_page_view=false to avoid double-counting with
 * Enhanced Measurement's history-change detection.
 */
export function trackPageView(params: {
  path: string;
  title?: string;
  location?: string;
}): void {
  const measurementId = getMeasurementId();
  const eventParams: GtagEventParams = {
    page_path: params.path,
  };
  if (params.location) eventParams.page_location = params.location;
  if (params.title) eventParams.page_title = params.title;
  if (measurementId) eventParams.send_to = measurementId;
  trackEvent("page_view", eventParams);
}

/**
 * Associates subsequent events with an authenticated user. Must be a stable,
 * non-PII identifier (the Firebase uid is safe).
 *
 * Clearing identity (sign-out) must be done via clearUserIdentity below.
 */
export function identifyUser(userId: string, props?: Record<string, string>): void {
  if (!userId || typeof userId !== "string") return;
  const configParams: GtagConfigParams = {
    user_id: userId,
    // Re-state send_page_view: false so this config call does not trigger an
    // implicit auto page_view. Without this, GA4 fires a synthetic page_view
    // for each auth-state change, which can become the first hit in a new
    // session on returning visitors — landing page then shows as "(not set)"
    // because the auto page_view's page_location is omitted by some gtag
    // internals before the PageViewTracker fires.
    send_page_view: false,
  };
  const measurementId = getMeasurementId();
  if (measurementId) {
    safeGtag("config", measurementId, configParams);
  }
  safeGtag("set", { user_id: userId });
  if (props && Object.keys(props).length > 0) {
    safeGtag("set", "user_properties", props as unknown as GtagConfigParams);
  }
}

/**
 * Clears the user identity previously set via identifyUser.
 */
export function clearUserIdentity(): void {
  const measurementId = getMeasurementId();
  if (measurementId) {
    safeGtag("config", measurementId, { user_id: null });
  }
  safeGtag("set", { user_id: null });
}

/**
 * Fires a custom event recording that the signup modal was presented. Useful
 * for measuring CTA-to-modal conversion independently of sign_up.
 *
 * Note on the removed ``trackSignUpConversion`` helper
 * ----------------------------------------------------
 * Earlier revisions of this module emitted a client-side GA4 ``sign_up``
 * event plus a direct Google Ads ``conversion`` send_to at signup time.
 * Both calls were removed in Phase I of the conversion-tracking rollout:
 *
 *   * ``sign_up`` is now fired server-side via GA4 Measurement Protocol
 *     from Composer (``composer/app/models/ga_measurement.py``) at the
 *     end of ``register_user()`` (web signup) and
 *     ``link_user_to_install_attribution()`` (desktop-first signup).
 *     Server-side is authoritative because it covers both surfaces and
 *     cannot be blocked by a post-signup navigation, ad-blocker, or
 *     tab close before the client-side beacon flushes.
 *   * The direct Google Ads ``gtag('event', 'conversion', { send_to })``
 *     call is no longer needed because ``sign_up`` is marked as a Key
 *     Event in GA4 Admin and GA4 auto-imports it into Google Ads via
 *     the linked account (see ``AW-11427848450`` GA4 link).
 *
 * If we ever need a client-side engagement ping for signup *attempts*
 * (as opposed to successful registrations), add a narrower event like
 * ``sign_up_attempt`` here rather than reintroducing ``sign_up`` — that
 * event name must remain owned by the server-side MP pipeline to
 * preserve deduplication.
 */
export function trackSignupModalOpened(source?: string): void {
  trackEvent("sign_up_modal", {
    event_category: "engagement",
    event_label: "Sign-Up Modal Opened",
    value: 1,
    ...(source ? { source } : {}),
  });
}

/**
 * Fires the GA4 `view_item_list` recommended event for a pricing/catalog
 * surface. Keeps the item_list_id stable so GA can roll up comparable views.
 */
export function trackViewItemList(params: {
  itemListId: string;
  itemListName: string;
  items: Array<{
    id: string;
    name: string;
    price?: number;
    currency?: string;
    index?: number;
  }>;
}): void {
  trackEvent("view_item_list", {
    item_list_id: params.itemListId,
    item_list_name: params.itemListName,
    items: params.items.map((i) => ({
      item_id: i.id,
      item_name: i.name,
      ...(i.price != null ? { price: i.price } : {}),
      ...(i.currency ? { currency: i.currency } : {}),
      ...(i.index != null ? { index: i.index } : {}),
    })),
  });
}

/**
 * Fires GA4 `begin_checkout` when the user initiates a checkout session.
 */
export function trackBeginCheckout(params: {
  itemId: string;
  itemName: string;
  value?: number;
  currency?: string;
}): void {
  trackEvent("begin_checkout", {
    ...(params.currency ? { currency: params.currency } : {}),
    ...(params.value != null ? { value: params.value } : {}),
    items: [
      {
        item_id: params.itemId,
        item_name: params.itemName,
        ...(params.value != null ? { price: params.value } : {}),
        quantity: 1,
      },
    ],
  });
}

/**
 * Fires GA4 `purchase`. transactionId SHOULD be stable per purchase so GA can
 * deduplicate. Callers pass the Stripe session_id or subscription id.
 */
export function trackPurchase(params: {
  transactionId: string;
  itemId: string;
  itemName: string;
  value?: number;
  currency?: string;
}): void {
  trackEvent("purchase", {
    transaction_id: params.transactionId,
    ...(params.currency ? { currency: params.currency } : {}),
    ...(params.value != null ? { value: params.value } : {}),
    items: [
      {
        item_id: params.itemId,
        item_name: params.itemName,
        ...(params.value != null ? { price: params.value } : {}),
        quantity: 1,
      },
    ],
  });
}

/**
 * Fires GA4 `file_download`. Used for installer downloads so we can attribute
 * desktop-install funnel conversions.
 */
export function trackFileDownload(params: {
  fileName: string;
  fileExtension?: string;
  linkUrl?: string;
  linkText?: string;
  extra?: Record<string, string | number | undefined>;
}): void {
  const extra: GtagEventParams = {};
  if (params.extra) {
    for (const [k, v] of Object.entries(params.extra)) {
      if (v != null && v !== "") extra[k] = v;
    }
  }
  trackEvent("file_download", {
    file_name: params.fileName,
    ...(params.fileExtension ? { file_extension: params.fileExtension } : {}),
    ...(params.linkUrl ? { link_url: params.linkUrl } : {}),
    ...(params.linkText ? { link_text: params.linkText } : {}),
    ...extra,
  });
}

export type DownloadOsTileClickEventName =
  | "download_os_tile_click_windows_x86_64"
  | "download_os_tile_click_macos_apple_silicon_arm64"
  | "download_os_tile_click_macos_intel_x86_64"
  | "download_os_tile_click_linux_x86_64";

/**
 * Maps download-platform slugs (route or manifest style) to explicit
 * OS/arch-specific event names.
 */
export function resolveDownloadOsTileClickEventName(
  platformSlug: string
): DownloadOsTileClickEventName | null {
  const normalized = platformSlug.trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === "windows" || normalized === "windows-x86_64") {
    return "download_os_tile_click_windows_x86_64";
  }
  if (normalized === "macos-silicon" || normalized === "darwin-aarch64") {
    return "download_os_tile_click_macos_apple_silicon_arm64";
  }
  if (normalized === "macos-intel" || normalized === "darwin-x86_64") {
    return "download_os_tile_click_macos_intel_x86_64";
  }
  if (normalized === "linux" || normalized === "linux-x86_64") {
    return "download_os_tile_click_linux_x86_64";
  }
  return null;
}

/**
 * Fires when a visitor clicks an OS-specific installer tile on `/nrs/download`
 * (for example macOS Apple Silicon or Windows). This is pre-auth top-of-funnel
 * intent and should not be used for "Download page CTA" clicks elsewhere.
 */
export function trackDownloadOsTileClick(params: {
  platformSlug: string;
  source?: string;
}): void {
  const eventName = resolveDownloadOsTileClickEventName(params.platformSlug);
  if (!eventName) {
    return;
  }
  trackEvent(eventName, {
    event_category: "engagement",
    event_label: "Download OS Tile Clicked",
    platform_slug: params.platformSlug,
    ...(params.source ? { source: params.source } : {}),
  });
}

/**
 * Fires when a user clicks any CTA that navigates to `/nrs/download` from
 * other pages (header, landing hero/footer, etc). This is separate from the
 * OS tile click event and intentionally does not include platform_slug.
 */
export function trackDownloadPageCtaClick(params?: { source?: string }): void {
  trackEvent("download_page_cta_click", {
    event_category: "engagement",
    event_label: "Download Page CTA Clicked",
    ...(params?.source ? { source: params.source } : {}),
  });
}

/**
 * Fires when a visitor picks a continuation path (Google / GitHub /
 * Neuraville email) from the shared `SignupModal` auth chooser. Lets us see,
 * of the people who reach the modal, which option they pick — independent of
 * whether they go on to complete authentication.
 */
export function trackAuthProviderSelected(params: {
  provider: "google" | "github" | "neuraville";
  source?: string;
}): void {
  trackEvent("auth_provider_selected", {
    event_category: "engagement",
    event_label: "Auth Provider Selected",
    provider: params.provider,
    ...(params.source ? { source: params.source } : {}),
  });
}

/**
 * Fires when an auth attempt (OAuth or email/password) is rejected before
 * the user reaches the installer. Tracked regardless of whether the failure
 * is "expected" (wrong password, existing account) or unexpected — both
 * represent real conversion drop-off in the download/signup funnel.
 * `reason` is a coarse, PII-free category from
 * `nrsAuthProviderClickTracking.classifyAuthFailureReason`.
 */
export function trackAuthAttemptFailed(params: {
  provider: "google" | "github" | "neuraville";
  reason: "invalid_credentials" | "account_conflict" | "consent_required" | "unknown_error";
  source?: string;
}): void {
  trackEvent("auth_attempt_failed", {
    event_category: "engagement",
    event_label: "Auth Attempt Failed",
    provider: params.provider,
    reason: params.reason,
    ...(params.source ? { source: params.source } : {}),
  });
}

/**
 * Fires when `NrsMobileDownloadEmailModal` becomes visible after a mobile
 * visitor taps a platform tile. Pairs with `trackMobileDownloadEmailSubmitted`
 * to measure modal-shown -> email-submitted conversion.
 */
export function trackMobileDownloadEmailModalShown(params: {
  platformSlug: string;
  source?: string;
}): void {
  trackEvent("mobile_download_email_modal_shown", {
    event_category: "engagement",
    event_label: "Mobile Download Email Modal Shown",
    platform_slug: params.platformSlug,
    ...(params.source ? { source: params.source } : {}),
  });
}

/**
 * Fires once the mobile download-link email request succeeds. Does not
 * include the submitted address — GA4 events must stay free of PII.
 */
export function trackMobileDownloadEmailSubmitted(params: {
  platformSlug: string;
  source?: string;
}): void {
  trackEvent("mobile_download_email_submitted", {
    event_category: "engagement",
    event_label: "Mobile Download Email Submitted",
    platform_slug: params.platformSlug,
    ...(params.source ? { source: params.source } : {}),
  });
}

/**
 * One-shot ops event for a `/server-down` visit. Do not fire this on each
 * health poll — page_view already records the landing. `cause` is how the
 * user arrived; `health_still_down` is the first health-check result.
 */
export function trackServerUnavailable(params: {
  cause: "api_failures" | "auth_500" | "direct";
  healthStillDown: boolean;
}): void {
  trackEvent("server_unavailable", {
    event_category: "ops",
    event_label: "Server Unavailable",
    cause: params.cause,
    health_still_down: params.healthStillDown,
  });
}

/**
 * Fires a generic feature engagement event. We use this for lab,
 * brain-visualizer, configurator, and embodiment page entries so we can
 * measure product-surface engagement independently of vanilla page_view.
 */
export function trackFeatureView(featureName: string): void {
  trackEvent("feature_view", {
    feature_name: featureName,
    event_category: "engagement",
  });
}

/**
 * Fires GA4 `click` with outbound=true for outbound link navigations.
 */
export function trackOutboundLink(params: {
  url: string;
  linkText?: string;
}): void {
  trackEvent("click", {
    outbound: true,
    link_url: params.url,
    link_domain: safeUrlDomain(params.url),
    ...(params.linkText ? { link_text: params.linkText } : {}),
  });
}

function safeUrlDomain(url: string): string | undefined {
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
}
